import React, {useState, useEffect, useContext} from 'react'
import {NotificationItem} from './NotificationItem'
import {Dropdown} from './Dropdown'
import {Icon} from './Icon'
import {UserContext} from './contexts'
import {listenForNotifications, getPlayerProfile} from './util/db'
import {useLocalStorage} from './hooks/useLocalStorage.js'
import {toast} from 'react-toastify'
import {getBadgeDetails} from '@cssbattle/shared/badges'
import {Badge} from '@components/Badge'
import {HStack} from '@components/Stack'

/**
 * Process each notification and fetch any associated data required
 * @param {array} notifications list of notifications
 */
async function processNotifications(notifications) {
  const promises = []
  notifications.forEach((notification) => {
    // Someone followed you
    if (notification.type === 'follow') {
      promises.push(
        //  Fetch follower's profile
        getPlayerProfile(notification.data.followerId).then((player) => {
          notification.data.follower = player
        })
      )
    }
  })
  await Promise.all(promises)
}

export function NotificationsBox() {
  const user = useContext(UserContext)

  const [notifications, setNotifications] = useState([])
  const [lastReadNotificationTimestamp, setLastReadNotificationTimestamp] =
    useLocalStorage('lastReadNotificationTimestamp', 0)
  const [unReadCount, setUnReadCount] = useState(0)

  useEffect(() => {
    if (!user || !user.uid) return
    const unListen = listenForNotifications(user.uid, async (notifications) => {
      await processNotifications(notifications)
      setNotifications(notifications)
      setUnReadCount(
        notifications.filter(
          (notification) =>
            notification.timestamp > lastReadNotificationTimestamp
        ).length
      )

      // Check for recent badge notifications and show a toast
      const now = Date.now()
      const oneMinuteAgo = now - 60 * 1000

      const isSoundPlayed = false
      notifications.forEach((notification) => {
        if (
          notification.type === 'badge' &&
          notification.timestamp > oneMinuteAgo
        ) {
          const badgeName = getBadgeDetails(notification.data.badgeId)?.name
          toast.success(
            <HStack gap={1}>
              <Badge
                badgeId={notification.data.badgeId}
                badgeMetadata={notification.data.metadata}
                size="small"
                showTooltip={false}
                particles={false}
              />
              <div>You earned the "{badgeName}" badge!</div>
            </HStack>,
            {
              icon: false,
            }
          )

          if (!isSoundPlayed) {
            const audio = new Audio('/sfx/badge.mp3')
            audio.play()
            isSoundPlayed = true
          }
        }
      })
    })

    return () => {
      unListen()
    }
  }, [user ? user.uid : ''])

  function openHandler() {
    if (!notifications.length) return
    setLastReadNotificationTimestamp(notifications[0].timestamp)
    setUnReadCount(0)
  }

  function renderNotificationItem(notification) {
    if (notification.type === 'follow') {
      return (
        <NotificationItem
          key={notification.id}
          type={notification.type}
          timeStamp={notification.timestamp}
          actionLink={`/player/${
            notification.data.follower.username || notification.data.followerId
          }`}
          imageUrl={notification.data.follower.avatar}
        >
          {notification.data.follower.displayName} added you as a friend!
        </NotificationItem>
      )
    } else if (notification.type === 'badge') {
      return (
        <NotificationItem
          key={notification.id}
          type={notification.type}
          timeStamp={notification.timestamp}
          badgeId={notification.data.badgeId}
          badgeMetadata={notification.data.metadata}
          actionLink={`/player/${user.username || user.id}`}
        />
      )
    }

    // Default case
    return null
  }

  return (
    <Dropdown
      alignRight={true}
      dropdownMenuClasses="dropdown-menu--notifications"
      dropdownBtnClasses="dropdown-btn--header"
      onOpen={openHandler}
      dropdownBtnContent={
        <>
          <Icon name="bell" size="24" marginRight="0" />
          {unReadCount ? (
            <div className="notifications-count  notifications-count--visible">
              {unReadCount}
            </div>
          ) : null}
        </>
      }
    >
      {!notifications.length ? (
        <p style={{padding: '1.25rem'}}>Nothing to see here.</p>
      ) : null}
      {notifications.map(renderNotificationItem)}
    </Dropdown>
  )
}
